/*
 * Slide Templates — turns a set of selected template ids into slides in the
 * deck. Thin wrapper around shared/services/pptxTemplateSlideInsert.ts's
 * `insertTemplateSlides` (source-file grouping, sub-deck building, insert
 * loop — identical to Presentation Templates' own need, see that module's
 * header) with this feature's own messages/telemetry reasons. Shared by both
 * callers so the ordering/partial-failure rules live in one place:
 *
 *   - hooks/useSlideTemplates.ts — the in-pane Asset Library, where the panel
 *     holds the full manifest itself.
 *   - asset-library/hooks/useAssetLibraryLauncher.ts — the dialog flow, where
 *     the pane holds the manifest and the ids come back over messageParent.
 *
 * It also owns the outcomes an insert can have and the copy shown for each
 * (Slide Templates BRD §8.8 and §10). Insertion runs in the task pane (an
 * Office Dialog can't call PowerPoint.run()) and the Asset Library window
 * closes as soon as it has an answer, so a failure can't be reported inside
 * the window — the pane keeps a notice with an "Open Asset Library" button.
 */

import {
  insertTemplateSlides,
  DeckReadOnlyError as SharedDeckReadOnlyError,
  SelectionUnavailableError as SharedSelectionUnavailableError,
  
  isDeckReadOnly as sharedIsDeckReadOnly,
  type InsertFailureKind,
  type InsertNoticeCopy,
} from "../../shared/services/pptxTemplateSlideInsert";
import { logSlideTemplatesError, type SlideTemplate } from "./slideTemplatesApi";

export type { InsertFailureKind, InsertNoticeCopy };

export const NO_INSERTABLE_SELECTION_MESSAGE = "None of the selected templates have insertable slide content yet.";
const SOURCE_DOWNLOAD_FAILED_MESSAGE = "Downloading this template's source file failed.";

/**
 * Inserts the selected templates, in the order the manifest lists them,
 * immediately after the slide currently selected in the deck.
 *
 * @returns how many slides were inserted.
 */
export async function insertSlideTemplates(
  templates: readonly SlideTemplate[],
  selectedIds: readonly string[],
): Promise<number> {
  return insertTemplateSlides(
    templates,
    selectedIds,
    { noInsertableSelection: NO_INSERTABLE_SELECTION_MESSAGE, sourceDownloadFailed: SOURCE_DOWNLOAD_FAILED_MESSAGE },
    { log: logSlideTemplatesError, reasonPrefix: "slide-templates" },
    // BRD 8.8: Slide Templates go immediately after the user's current slide.
    { insertAfterCurrent: true },
  );
}

/* ── Insert outcomes ─────────────────────────────────────────────── */

export const INSERT_NOTICE_COPY: Readonly<Record<InsertFailureKind, InsertNoticeCopy>> = {
  readonly: {
    title: "This presentation can't be edited.",
    message: "It's read-only, so nothing was added. Make it editable in PowerPoint, then try again.",
  },
  connection: {
    title: "Connection lost",
    message: "Nothing was added to your presentation. Check your connection, then try again.",
  },
  removed: {
    title: "That slide template is no longer available",
    message: "Your organisation updated the library, so nothing was inserted. Choose another one instead.",
  },
};

/** The in-pane confirmation after a successful insert (BRD §8.8). */
export function slideTemplatesAddedMessage(count: number): string {
  return count === 1
    ? "1 slide template added after your current slide"
    : `${count} slide templates added after your current slide`;
}

/** Thrown when a selected template is no longer in the organisation's library. */
export class SelectionUnavailableError extends SharedSelectionUnavailableError {
  constructor() {
    super(INSERT_NOTICE_COPY.removed.message);
  }
}

/** Thrown before an insert is attempted on a read-only presentation. */
export class DeckReadOnlyError extends SharedDeckReadOnlyError {
  constructor() {
    super(INSERT_NOTICE_COPY.readonly.message);
  }
}

/** True when PowerPoint reports the open presentation as read-only. */
export function isDeckReadOnly(): boolean {
  return sharedIsDeckReadOnly(logSlideTemplatesError, "slide-templates-read-only-check");
}



export {classifyInsertFailure} from "../../shared/services/pptxTemplateSlideInsert";